import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import SummaryRow from "../Portfolio/SummaryRow";
import AccountGrid from "../Portfolio/AccountGrid";
import ConnectionHealthBanner from "../ui/ConnectionHealthBanner";
import DemoCTA from "./DemoCTA";
import DemoAccountDetail from "./DemoAccountDetail";
import DemoSavingsRateCard from "./DemoSavingsRateCard";
import DemoFiNumberCard from "./DemoFiNumberCard";
import DemoSpendingBreakdownCard from "./DemoSpendingBreakdownCard";
import { useDemo } from "./DemoContext";

function SectionHeader({ title, subtitle }) {
    return (
        <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
    );
}

export default function DemoPortfolioTab() {
    const location = useLocation();
    const {
        visibleAccounts,
        visibleHoldings,
        netWorthData,
        netWorthHistory,
        connectionHealth,
        emergencyFundData,
        updateEmergencyFund
    } = useDemo();

    const [selectedAccount, setSelectedAccount] = useState(null);
    const [financialPeriod, setFinancialPeriod] = useState(6);

    // Reset selected account when navigating to this page (e.g., clicking Dashboard in sidebar)
    useEffect(() => {
        setSelectedAccount(null);
    }, [location.key]);

    // Show account detail when an account is selected
    if (selectedAccount) {
        return (
            <DemoAccountDetail
                account={selectedAccount}
                onBack={() => setSelectedAccount(null)}
            />
        );
    }

    return (
        <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                        Hi User, here's your portfolio
                    </h1>
                    <p className="text-gray-500 mt-0.5 text-sm">Your wealth at a glance</p>
                </div>
            </div>

            {/* Connection Health Banner */}
            <ConnectionHealthBanner healthSummary={connectionHealth.summary} />

            {/* Section 1: Net Worth Overview */}
            <SummaryRow
                netWorthData={netWorthData}
                historyData={netWorthHistory}
                emergencyFundData={emergencyFundData}
                onEmergencyFundUpdate={updateEmergencyFund}
                forceShowEmergencyFund={true}
            />

            {/* Section 2: Financial Health */}
            <div className="mt-10">
                <SectionHeader
                    title="Financial Health"
                    subtitle="Your savings rate, spending patterns, and path to FI"
                />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <DemoSavingsRateCard months={financialPeriod} onMonthsChange={setFinancialPeriod} />
                    <DemoFiNumberCard />
                </div>
                <div className="mt-6">
                    <DemoSpendingBreakdownCard months={financialPeriod} onMonthsChange={setFinancialPeriod} />
                </div>
            </div>

            {/* Section 3: Accounts */}
            <div className="mt-10">
                <SectionHeader
                    title="Accounts"
                    subtitle={`${visibleAccounts.length} linked account${visibleAccounts.length !== 1 ? "s" : ""}`}
                />
                <AccountGrid
                    accounts={visibleAccounts}
                    holdings={visibleHoldings}
                    onSelect={setSelectedAccount}
                />
            </div>

            <div className="mt-6">
                <DemoCTA variant="inline" />
            </div>
        </div>
    );
}
